// Discovery Engine Phase 3B — pilot ACCESS + PERSISTENCE policy (fail-closed).
//
// Two independent, PURE decisions plus thin runtime readers. Lives in config/ (no
// runtime/services import) so the always-loaded shell can gate the lazy bundle.
//
//   ACCESS  = global gate (incl. kill) AND the pilot-cohort gate. Default-deny when
//             cohort enforcement is on; the global kill ALWAYS overrides cohort.
//   PERSIST = production-enabled Discovery MUST use the authorized durable (Postgres)
//             repository. If durable is required but not configured → UNAVAILABLE
//             (fail closed) — it NEVER silently falls back to local/in-memory.
//
// The client never MINTS the cohort decision; it ENFORCES a claim supplied by a
// trusted boundary (an authenticated server/edge response in production; injected in
// tests). The pure deciders take their inputs explicitly so every branch is testable.
//
// ⚠ THIS IS UX + BUNDLE CONTROL ONLY — NOT THE AUTHORIZATION BOUNDARY. The
// authoritative pilot authorization is server-enforced inside the Discovery write
// RPCs (migration 0077: `discovery_write_allowed()` gating `finalize_discovery_run`
// + `register_discovery_prior` on the server-side global gate + active pilot
// membership). A user who bypasses this UI and calls the RPC directly STILL cannot
// create durable Discovery records unless the server authorizes them. Do not treat
// these client decisions as a security control.

import { publicEnv } from './clientEnv';
import { discoveryGate, type DiscoveryGate } from './discoveryFlag';

/** Repository persistence mode (kept in sync with the services factory union). */
export type DiscoveryPersistMode = 'memory' | 'local' | 'postgres';

/** A cohort decision from the trusted boundary. `trusted` is false when the claim is
 *  absent/forged-shaped — treated as not-in-cohort (fail closed). */
export interface CohortClaim { inCohort: boolean; trusted: boolean; cohortId?: string }

export interface AccessInput { gate: DiscoveryGate; enforceCohort: boolean; claim: CohortClaim | null }
export interface AccessDecision { allowed: boolean; reason: string }

/** PURE access decision: global gate AND cohort. Global kill/disabled overrides any
 *  cohort inclusion; an enforced cohort with a missing/untrusted/non-member claim
 *  fails closed. */
export function decideAccess(input: AccessInput): AccessDecision {
  if (!input.gate.enabled) return { allowed: false, reason: input.gate.reason }; // global kill / disabled wins
  if (!input.enforceCohort) return { allowed: true, reason: 'cohort_not_enforced' };
  const c = input.claim;
  if (!c || c.trusted !== true) return { allowed: false, reason: 'cohort_claim_untrusted_or_missing' };
  if (c.inCohort !== true) return { allowed: false, reason: 'not_in_cohort' };
  return { allowed: true, reason: 'in_cohort' };
}

export interface PersistenceInput { requireDurable: boolean; postgresConfigured: boolean }
export type PersistenceDecision =
  | { status: 'available'; mode: DiscoveryPersistMode; durable: boolean; reason: string }
  | { status: 'unavailable'; reason: string };

/** PURE persistence decision: when durable is required (production pilot) the
 *  authorized Postgres repo MUST be configured, else fail closed — never local. */
export function decidePersistence(input: PersistenceInput): PersistenceDecision {
  if (input.requireDurable) {
    if (!input.postgresConfigured) return { status: 'unavailable', reason: 'durable_repository_not_configured' };
    return { status: 'available', mode: 'postgres', durable: true, reason: 'production_durable' };
  }
  return { status: 'available', mode: 'local', durable: false, reason: 'development_local' };
}

// ── runtime signal readers (thin; impure) ─────────────────────────────────────
export const COHORT_CLAIM_KEY = 'discovery_cohort';
export const COHORT_ENFORCE_KEY = 'discovery_cohort_enforce';
export const REQUIRE_DURABLE_KEY = 'discovery_require_durable';

function ls(key: string): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; } catch { return null; }
}

export function cohortEnforced(): boolean {
  const env = (publicEnv('VITE_DISCOVERY_COHORT') ?? '').toLowerCase();
  if (env === 'on') return true;
  if (env === 'off') return false;
  return ls(COHORT_ENFORCE_KEY) === '1';
}

export function readCohortClaim(): CohortClaim | null {
  const raw = ls(COHORT_CLAIM_KEY);
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    return { inCohort: o.inCohort === true, trusted: o.trusted === true, cohortId: typeof o.cohortId === 'string' ? o.cohortId : undefined };
  } catch { return null; } // malformed claim → null → fail closed when enforced
}

export function durableRequired(): boolean {
  const env = (publicEnv('VITE_DISCOVERY_PERSISTENCE') ?? '').toLowerCase();
  if (env === 'durable') return true;
  if (env === 'local' || env === 'memory') return false;
  return ls(REQUIRE_DURABLE_KEY) === '1';
}

/** Composed runtime resolvers used by the UI. */
export function resolveDiscoveryAccess(): AccessDecision {
  return decideAccess({ gate: discoveryGate(), enforceCohort: cohortEnforced(), claim: readCohortClaim() });
}
/** `postgresConfigured` is supplied by the composition root; in Phase 3B repo-side
 *  no Postgres port is wired, so durable-required ⇒ unavailable (fail closed). */
export function resolveDiscoveryPersistence(postgresConfigured = false): PersistenceDecision {
  return decidePersistence({ requireDurable: durableRequired(), postgresConfigured });
}
